import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Loader2, Save, Copy, MessageCircle, CheckCircle2, UserPlus, Send, ExternalLink, Ban } from "lucide-react";

const DEFAULT_DEPOSIT_PERCENT_OPTIONS = [10, 20, 30, 40, 50];
const DEPOSIT_PERCENT_OPTIONS_STORAGE_KEY = "rental-deposit-percent-options";
import { PAYMENT_METHODS, paymentMethodChannel, paymentMethodLabel } from "@/lib/payment-methods";
import {
  getRentalSettings, saveRentalSettings,
  listRentalModalities, saveRentalModality, deleteRentalModality,
  listRentalPrices, saveRentalPrice, deleteRentalPrice,
  listWeekBusy, saveRental, deleteRental,
  listRentalClients, saveRentalClient,
  listRentalBlackouts, saveRentalBlackout, deleteRentalBlackout,
} from "@/lib/api/rentals.functions";

import { sendWhatsappSequence } from "@/lib/api/whatsapp.functions";
import { getBillingPixInfo } from "@/lib/api/charges.functions";
import { buildPixBrCode } from "@/lib/pix/brcode";
import { supabase } from "@/integrations/supabase/client";
import { brIsoDate, brMinutes, brDow, brTime, brDateLabel, brIsoFrom, brToday, brStartOfWeek, addAnchorDays, anchorIso } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/m/locacao")({ component: Page });

const DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// `addDays` mantido como apelido para `addAnchorDays` — navegação de semana opera sobre âncoras (meio-dia UTC).
function addDays(d: Date, n: number) { return addAnchorDays(d, n); }
// Formata dia/mês a partir da âncora, sem depender do fuso do navegador.
function fmtDate(d: Date) { const [, m, dd] = anchorIso(d).split("-"); return `${dd}/${m}`; }
function toMinutes(t: string) { const [h, m] = t.split(":").map(Number); return h * 60 + m; }
function minutesToHHMM(m: number) {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

const DURATION_OPTIONS = [30, 60, 90, 120];
function durationLabel(m: number) {
  const h = Math.floor(m / 60), mm = m % 60;
  if (h === 0) return `${mm} min`;
  if (mm === 0) return h === 1 ? "1 hora" : `${h} horas`;
  return `${h}h ${mm}min`;
}

function Page() {
  const [tab, setTab] = useState("calendario");

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex items-start gap-4">
        <div className="rounded-xl bg-primary/15 p-3 text-primary"><KeyRound className="h-7 w-7" /></div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Reserva de Espaço</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Parametrize espaços, valores e reserve horários no calendário semanal.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="calendario">Calendário</TabsTrigger>
          <TabsTrigger value="parametros">Parâmetros</TabsTrigger>
          <TabsTrigger value="modalidades">Cadastro de Espaço</TabsTrigger>
          <TabsTrigger value="precos">Valores</TabsTrigger>
        </TabsList>
        <TabsContent value="calendario"><CalendarTab /></TabsContent>
        <TabsContent value="parametros"><ParametersTab /></TabsContent>
        <TabsContent value="modalidades"><ModalitiesTab /></TabsContent>
        <TabsContent value="precos"><PricesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============ PARAMETERS ============ */

function ParametersTab() {
  const getFn = useServerFn(getRentalSettings);
  const saveFn = useServerFn(saveRentalSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slotMin, setSlotMin] = useState(60);
  const [options, setOptions] = useState<number[]>([]);
  const [startT, setStartT] = useState("06:00");
  const [endT, setEndT] = useState("23:00");
  const [pm, setPm] = useState<string[]>([]);
  const [allowPublic, setAllowPublic] = useState(false);
  const [requireDeposit, setRequireDeposit] = useState(false);
  const [depositPercent, setDepositPercent] = useState(0);
  const [depositPercentOptions, setDepositPercentOptions] = useState<number[]>(() => {
    if (typeof window === "undefined") return DEFAULT_DEPOSIT_PERCENT_OPTIONS;
    try {
      const raw = window.localStorage.getItem(DEPOSIT_PERCENT_OPTIONS_STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as number[]) : [];
      const merged = Array.from(new Set([...DEFAULT_DEPOSIT_PERCENT_OPTIONS, ...parsed])).sort((a, b) => a - b);
      return merged;
    } catch {
      return DEFAULT_DEPOSIT_PERCENT_OPTIONS;
    }
  });

  function addCustomDepositPercent() {
    const raw = window.prompt("Informe o novo percentual de sinal (%)");
    if (raw == null) return;
    const value = Number(raw.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0 || value > 100) {
      toast.error("Informe um percentual válido entre 1 e 100.");
      return;
    }
    setDepositPercentOptions((prev) => {
      const next = Array.from(new Set([...prev, value])).sort((a, b) => a - b);
      try {
        window.localStorage.setItem(DEPOSIT_PERCENT_OPTIONS_STORAGE_KEY, JSON.stringify(next.filter((v) => !DEFAULT_DEPOSIT_PERCENT_OPTIONS.includes(v))));
      } catch {}
      return next;
    });
    setDepositPercent(value);
  }
  const [depositMethod, setDepositMethod] = useState<string>("pix");
  const [publicPm, setPublicPm] = useState<string[]>([]);
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [halfHour, setHalfHour] = useState(true);

  useEffect(() => { (async () => {
    try {
      const r = await getFn();
      const s = r.settings;
      setSlotMin(s.slot_duration_min);
      setOptions(s.slot_duration_options ?? []);
      setStartT((s.start_time as string).slice(0, 5));
      setEndT((s.end_time as string).slice(0, 5));
      setPm(s.accepted_payment_methods ?? []);
      setAllowPublic(s.allow_public_booking ?? false);
      setRequireDeposit(s.require_deposit ?? false);
      const loadedDepositPercent = s.deposit_percent ?? 0;
      setDepositPercent(loadedDepositPercent);
      if (loadedDepositPercent > 0) {
        setDepositPercentOptions((prev) => Array.from(new Set([...prev, loadedDepositPercent])).sort((a, b) => a - b));
      }
      setDepositMethod(s.deposit_payment_method ?? "pix");
      setPublicPm(s.public_payment_methods ?? []);
      setAutoConfirm(s.auto_confirm_after_payment ?? true);
      setHalfHour((s as any).allow_half_hour_slots ?? true);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setLoading(false); }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveFn({ data: {
        slot_duration_min: slotMin,
        slot_duration_options: options,
        start_time: startT,
        end_time: endT,
        accepted_payment_methods: pm,
        allow_public_booking: allowPublic,
        require_deposit: requireDeposit,
        deposit_percent: requireDeposit ? depositPercent : 0,
        deposit_payment_method: requireDeposit ? depositMethod : null,
        public_payment_methods: allowPublic ? publicPm : [],
        auto_confirm_after_payment: autoConfirm,
        allow_half_hour_slots: halfHour,
      }});
      toast.success("Parâmetros salvos");
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin mx-auto mt-6" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Parâmetros de Reserva de Espaço</CardTitle>
        <CardDescription>Configure os horários de funcionamento, as formas de pagamento e o autoatendimento pelo portal do usuário.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Hora de início das locações</Label>
            <Input type="time" value={startT} onChange={(e) => setStartT(e.target.value)} />
          </div>
          <div>
            <Label>Hora final das locações</Label>
            <Input type="time" value={endT} onChange={(e) => setEndT(e.target.value)} />
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Formas de pagamento aceitas (interno)</Label>
          <div className="grid gap-2 sm:grid-cols-2">
            {PAYMENT_METHODS.map((m) => (
              <label key={m.value} className="flex items-center gap-2 p-3 border rounded-md hover:bg-secondary/30 cursor-pointer">
                <Checkbox checked={pm.includes(m.value)}
                  onCheckedChange={(c) => setPm(c ? [...pm, m.value] : pm.filter((x) => x !== m.value))} />
                <span className="text-sm">{m.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="border rounded-md p-4 flex items-center justify-between gap-4">
          <div>
            <Label className="text-base">Quebrar o horário a cada 30 minutos</Label>
            <p className="text-sm text-muted-foreground">
              Quando ativo, os horários do calendário podem começar a cada 30 minutos (ex.: 18:30–19:30). Desativado, só começam de acordo com a duração padrão do slot.
            </p>
          </div>
          <Switch checked={halfHour} onCheckedChange={setHalfHour} />
        </div>

        <div className="border rounded-md p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Permitir reserva pelo portal do usuário</Label>
              <p className="text-sm text-muted-foreground">Responsáveis vinculados podem reservar espaços pelo app.</p>
              <div className="mt-2">
                {allowPublic && publicPm.length > 0 ? (
                  <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700">Reserva no app: ativa</span>
                ) : allowPublic ? (
                  <span className="inline-block rounded px-2 py-0.5 text-xs bg-amber-100 text-amber-800">Escolha ao menos uma forma de pagamento para ativar</span>
                ) : (
                  <span className="inline-block rounded px-2 py-0.5 text-xs bg-muted text-muted-foreground">Usuários veem valores e horários, mas não podem reservar</span>
                )}
              </div>
            </div>
            <Switch checked={allowPublic} onCheckedChange={setAllowPublic} />
          </div>

          {allowPublic && (

            <div className="space-y-4 border-t pt-4">
              <div>
                <Label className="mb-2 block">Formas de pagamento disponíveis no app</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {PAYMENT_METHODS.map((m) => (
                    <label key={m.value} className="flex items-center gap-2 p-3 border rounded-md hover:bg-secondary/30 cursor-pointer">
                      <Checkbox checked={publicPm.includes(m.value)}
                        onCheckedChange={(c) => setPublicPm(c ? [...publicPm, m.value] : publicPm.filter((x) => x !== m.value))} />
                      <span className="text-sm">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Exigir sinal para reserva</Label>
                  <p className="text-sm text-muted-foreground">Bloqueia o horário e exige pagamento antecipado.</p>
                </div>
                <Switch checked={requireDeposit} onCheckedChange={setRequireDeposit} />
              </div>

              {requireDeposit && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Percentual do sinal (%)</Label>
                    <div className="flex items-center gap-2">
                      <Select value={String(depositPercent)} onValueChange={(v) => setDepositPercent(Number(v))}>
                        <SelectTrigger><SelectValue placeholder="Selecione o percentual" /></SelectTrigger>
                        <SelectContent>
                          {depositPercentOptions.map((p) => (
                            <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={addCustomDepositPercent} title="Adicionar novo percentual">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Forma de pagamento do sinal</Label>
                    <Select value={depositMethod} onValueChange={setDepositMethod}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Confirmar automaticamente após pagamento</Label>
                  <p className="text-sm text-muted-foreground">Marca a reserva como confirmada quando o pagamento for aprovado.</p>
                </div>
                <Switch checked={autoConfirm} onCheckedChange={setAutoConfirm} />
              </div>
            </div>
          )}
        </div>

        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Parâmetros
        </Button>
      </CardContent>
    </Card>
  );
}

/* ============ MODALITIES ============ */

function ModalitiesTab() {
  const listFn = useServerFn(listRentalModalities);
  const saveFn = useServerFn(saveRentalModality);
  const delFn = useServerFn(deleteRentalModality);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try { const r = await listFn(); setItems(r.modalities); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    const defaults = [
      { name: "Campo Society", field_size: "" },
      { name: "Quadra Futsal", field_size: "" },
      { name: "Campo de Futebol", field_size: "" },
    ];
    for (const d of defaults) await saveFn({ data: { ...d, active: true } as any });
    toast.success("Modalidades padrão criadas");
    load();
  };

  useEffect(() => {
    if (!loading && items.length === 0) seedDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const openNew = () => { setEditing({ id: null, name: "", field_size: "", address: "", cep: "", street: "", number: "", neighborhood: "", city: "", state: "", latitude: null, longitude: null, active: true }); setOpen(true); };
  const openEdit = (m: any) => { setEditing({ ...m }); setOpen(true); };

  const composeAddress = (e: any) => {
    const line1 = [e.street, e.number].filter(Boolean).join(", ");
    const line2 = [e.neighborhood, e.city && e.state ? `${e.city}/${e.state}` : (e.city || e.state)].filter(Boolean).join(" - ");
    const cep = e.cep ? `CEP ${e.cep}` : "";
    return [line1, line2, cep].filter(Boolean).join(", ");
  };

  const [cepLoading, setCepLoading] = useState(false);
  const fetchCep = async (raw: string) => {
    const cep = (raw || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const j = await r.json();
      if (j?.erro) { toast.error("CEP não encontrado"); return; }
      setEditing((prev: any) => ({
        ...prev,
        cep,
        street: j.logradouro || prev.street || "",
        neighborhood: j.bairro || prev.neighborhood || "",
        city: j.localidade || prev.city || "",
        state: j.uf || prev.state || "",
      }));
    } catch { toast.error("Falha ao buscar CEP"); }
    finally { setCepLoading(false); }
  };

  const [geoLoading, setGeoLoading] = useState(false);
  const captureCoords = () => {
    if (!navigator.geolocation) return toast.error("Geolocalização indisponível");
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setEditing((prev: any) => ({ ...prev, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success("Coordenadas capturadas");
        setGeoLoading(false);
      },
      (err) => { toast.error(err.message || "Falha ao obter localização"); setGeoLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Informe o nome");
    try {
      const composed = composeAddress(editing) || editing.address || null;
      await saveFn({ data: {
        id: editing.id, name: editing.name, field_size: editing.field_size ?? null,
        address: composed,
        cep: editing.cep || null, street: editing.street || null, number: editing.number || null,
        neighborhood: editing.neighborhood || null, city: editing.city || null, state: editing.state || null,
        latitude: editing.latitude ?? null, longitude: editing.longitude ?? null,
        active: !!editing.active,
      }});
      setOpen(false); load();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const remove = async (id: string) => {
    if (!confirm("Excluir espaço?")) return;
    try { await delFn({ data: { id } }); load(); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Cadastro de Espaço</CardTitle>
          <CardDescription>Cadastre os espaços disponíveis para reserva. Espaços sem tamanho preenchido ficam ocultos para clientes.</CardDescription>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Novo espaço</Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
          <div className="space-y-2">
            {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nenhum espaço cadastrado.</p>}
            {items.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <p className="font-medium">{m.name} {m.active
                    ? <Badge className="ml-2 bg-green-600 hover:bg-green-600 text-white">Ativo</Badge>
                    : <Badge className="ml-2 bg-red-600 hover:bg-red-600 text-white">Inativo</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {m.field_size ? `Tamanho: ${m.field_size}` : <span className="italic">sem tamanho — oculto para clientes</span>}
                  </p>
                  {m.address && <p className="text-xs text-muted-foreground">📍 {m.address}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" title="Duplicar" onClick={() => openEdit({ ...m, id: null, name: `${m.name} (cópia)` })}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" title="Excluir" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar" : "Novo"} espaço</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nome *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div>
                <Label>Tamanho do campo/quadra</Label>
                <Input placeholder="Ex.: 40x20m" value={editing.field_size ?? ""} onChange={(e) => setEditing({ ...editing, field_size: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">Se em branco, este espaço não aparece para clientes.</p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>CEP</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="00000-000"
                      value={editing.cep ?? ""}
                      onChange={(e) => setEditing({ ...editing, cep: e.target.value })}
                      onBlur={(e) => fetchCep(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fetchCep(editing.cep ?? "")}
                      disabled={cepLoading}
                      className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700 ring-2 ring-blue-200 focus-visible:ring-blue-400"
                    >
                      {cepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buscar"}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>Logradouro</Label>
                  <Input value={editing.street ?? ""} onChange={(e) => setEditing({ ...editing, street: e.target.value })} />
                </div>
                <div>
                  <Label>Número</Label>
                  <Input value={editing.number ?? ""} onChange={(e) => setEditing({ ...editing, number: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Bairro</Label>
                <Input value={editing.neighborhood ?? ""} onChange={(e) => setEditing({ ...editing, neighborhood: e.target.value })} />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>Cidade</Label>
                  <Input value={editing.city ?? ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Input maxLength={2} value={editing.state ?? ""} onChange={(e) => setEditing({ ...editing, state: e.target.value.toUpperCase() })} />
                </div>
              </div>

              <div className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="mb-0">Localização (coordenadas)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={captureCoords} disabled={geoLoading}>
                    {geoLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : "📍"} Usar minha localização
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Latitude" value={editing.latitude ?? ""} onChange={(e) => setEditing({ ...editing, latitude: e.target.value === "" ? null : Number(e.target.value) })} />
                  <Input placeholder="Longitude" value={editing.longitude ?? ""} onChange={(e) => setEditing({ ...editing, longitude: e.target.value === "" ? null : Number(e.target.value) })} />
                </div>
                {(editing.latitude != null && editing.longitude != null) && (
                  <a
                    className="text-xs text-primary underline"
                    href={`https://www.google.com/maps?q=${editing.latitude},${editing.longitude}`}
                    target="_blank" rel="noreferrer"
                  >
                    Ver no Google Maps
                  </a>
                )}
                <p className="text-xs text-muted-foreground">Envie coordenadas para gerar o link do mapa na mensagem de confirmação.</p>
              </div>

              <label className="flex items-center gap-2">
                <Checkbox checked={editing.active} onCheckedChange={(c) => setEditing({ ...editing, active: !!c })} />
                <span className="text-sm">Ativo</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ============ PRICES ============ */

function PricesTab() {
  const listFn = useServerFn(listRentalPrices);
  const listModFn = useServerFn(listRentalModalities);
  const saveFn = useServerFn(saveRentalPrice);
  const delFn = useServerFn(deleteRentalPrice);
  const [items, setItems] = useState<any[]>([]);
  const [mods, setMods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([listFn(), listModFn()]);
      setItems(p.prices); setMods(m.modalities);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({
      id: null, modality_id: mods[0]?.id ?? null, rental_type: "avulsa",
      duration_min: 60, price: 0, discount: 0, payment_methods: ["pix", "cartao"],
    });
    setOpen(true);
  };

  const openEdit = (p: any) => { setEditing({ ...p }); setOpen(true); };
  const duplicate = (p: any) => {
    setEditing({ ...p, id: null });
    setOpen(true);
  };

  const save = async () => {
    try {
      await saveFn({ data: {
        id: editing.id, modality_id: editing.modality_id, rental_type: editing.rental_type,
        duration_min: Number(editing.duration_min ?? 60),
        price: Number(editing.price ?? 0), discount: Number(editing.discount ?? 0),
        payment_methods: editing.payment_methods ?? [],
        day_of_week: null,
      }});
      setOpen(false); load();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const remove = async (id: string) => {
    if (!confirm("Excluir valor?")) return;
    try { await delFn({ data: { id } }); load(); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const modName = (id: string | null) => mods.find((m) => m.id === id)?.name ?? "—";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Valores de Locação</CardTitle>
          <CardDescription>Defina o valor por modalidade e tipo (avulsa / mensal / anual).</CardDescription>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Cadastrar valor</Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
          <div className="space-y-2">
            {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nenhum valor cadastrado.</p>}
            {items.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 border rounded-md">
                <div className="space-y-1">
                  <p className="font-medium flex flex-wrap items-center gap-1">
                    <span className="text-blue-600">{modName(p.modality_id)}</span>
                    <Badge variant="outline" className="capitalize border-green-600 text-green-600">{p.rental_type}</Badge>
                    <Badge variant="outline" className="border-blue-600 text-blue-600">{durationLabel(p.duration_min ?? 60)}</Badge>
                  </p>
                  <p className="text-sm font-semibold text-red-600">
                    R$ {Number(p.price).toFixed(2)}
                    {p.rental_type === "mensal" && (
                      <span className="text-amber-700 font-medium"> / por locação</span>
                    )}
                    {Number(p.discount) > 0 && <span className="text-muted-foreground font-normal"> · desconto R$ {Number(p.discount).toFixed(2)}</span>}
                  </p>
                  {p.rental_type === "mensal" && (
                    <p className="text-xs rounded-md border border-amber-500 bg-amber-50 text-amber-800 px-2 py-1 font-medium">
                      Periodicidade <b>mensal</b>: o valor informado é cobrado <b>para cada locação</b> do mês (por sessão), não pelo mês inteiro.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">Pagamentos: {(p.payment_methods ?? []).join(", ")}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" title="Duplicar" onClick={() => duplicate(p)}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" title="Excluir" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Editar" : "Novo"} valor</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Espaço</Label>
                <Select value={editing.modality_id ?? ""} onValueChange={(v) => setEditing({ ...editing, modality_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                  <SelectContent>
                    {mods.filter((m) => m.active).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de locação</Label>
                <Select value={editing.rental_type} onValueChange={(v) => setEditing({ ...editing, rental_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avulsa">Avulsa</SelectItem>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editing.rental_type === "mensal" && (
                <p className="text-xs text-muted-foreground">
                  O dia da semana é definido pelo horário escolhido no calendário. O valor total mensal será calculado automaticamente pelas datas reservadas.
                </p>
              )}
              <div>
                <Label>Duração</Label>
                <Select value={String(editing.duration_min ?? 60)} onValueChange={(v) => setEditing({ ...editing, duration_min: parseInt(v, 10) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((d) => <SelectItem key={d} value={String(d)}>{durationLabel(d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
                <div><Label>Desconto (R$)</Label><Input type="number" step="0.01" value={editing.discount} onChange={(e) => setEditing({ ...editing, discount: e.target.value })} /></div>
              </div>
              <div>
                <Label className="mb-2 block">Formas de pagamento aceitas</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {PAYMENT_METHODS.map((m) => (
                    <label key={m.value} className="flex items-center gap-2 p-2 border rounded-md cursor-pointer">
                      <Checkbox checked={(editing.payment_methods ?? []).includes(m.value)}
                        onCheckedChange={(c) => {
                          const cur = editing.payment_methods ?? [];
                          setEditing({ ...editing, payment_methods: c ? [...cur, m.value] : cur.filter((x: string) => x !== m.value) });
                        }} />
                      <span className="text-sm">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ============ CALENDAR ============ */

type Busy =
  | { kind: "rental"; id: string; modalityId: string | null; startMin: number; endMin: number; title: string; sub?: string; rental_type?: string }
  | { kind: "training"; id: string; modalityId: string | null; startMin: number; endMin: number; title: string; sub?: string }
  | { kind: "event"; id: string; modalityId: string | null; startMin: number; endMin: number; title: string; sub?: string }
  | { kind: "blackout"; id: string; modalityId: string | null; startMin: number; endMin: number; title: string; sub?: string; blackoutKind: "one_off" | "weekly" };


function CalendarTab() {
  const getSettings = useServerFn(getRentalSettings);
  const listModFn = useServerFn(listRentalModalities);
  const listPricesFn = useServerFn(listRentalPrices);
  const listBusyFn = useServerFn(listWeekBusy);
  const saveFn = useServerFn(saveRental);
  const delFn = useServerFn(deleteRental);
  const listClientsFn = useServerFn(listRentalClients);
  const saveClientFn = useServerFn(saveRentalClient);
  const savePriceFn = useServerFn(saveRentalPrice);
  const sendSeqFn = useServerFn(sendWhatsappSequence);
  const pixInfoFn = useServerFn(getBillingPixInfo);
  const saveBlackoutFn = useServerFn(saveRentalBlackout);
  const deleteBlackoutFn = useServerFn(deleteRentalBlackout);

  const [weekStart, setWeekStart] = useState<Date>(brStartOfWeek(brToday()));
  const [settings, setSettings] = useState<any>(null);
  const [mods, setMods] = useState<any[]>([]);
  const [prices, setPrices] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [busy, setBusy] = useState<{ rentals: any[]; trainings: any[]; events: any[]; blackouts: any[] }>({ rentals: [], trainings: [], events: [], blackouts: [] });
  const [blackoutModal, setBlackoutModal] = useState<null | { id?: string; kind: "one_off" | "weekly"; block_date: string; day_of_week: number; start_time: string; end_time: string; reason: string; modality_id: string | null; _dateLabel?: string }>(null);

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pastAction, setPastAction] = useState<"save" | "confirm" | null>(null);
  const [pastAck, setPastAck] = useState(false);

  const [editing, setEditing] = useState<any>(null);
  const [clientOpen, setClientOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>({ name: "", cpf: "", phone: "" });
  const [missingPrice, setMissingPrice] = useState<null | { duration_min: number; price: string }>(null);
  const [waModal, setWaModal] = useState<null | {
    rental: any;
    phone: string;
    messages: string[];
    loading: boolean;
    sending: boolean;
  }>(null);

  const activeMods = useMemo(() => mods.filter((m) => m.active), [mods]);

  const loadClients = async () => {
    try { const r = await listClientsFn(); setClients(r.clients); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const loadStatic = async () => {
    try {
      const [s, m, p, c] = await Promise.all([getSettings(), listModFn(), listPricesFn(), listClientsFn()]);
      setSettings(s.settings); setMods(m.modalities); setPrices(p.prices); setClients(c.clients);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const saveClient = async () => {
    if (!editingClient.name?.trim()) return toast.error("Informe o nome");
    if (!editingClient.phone?.trim()) return toast.error("Informe o WhatsApp");
    try {
      const res = await saveClientFn({ data: {
        id: editingClient.id ?? null,
        name: editingClient.name.trim(),
        cpf: editingClient.cpf?.trim() || null,
        phone: editingClient.phone.trim(),
      }});
      await loadClients();
      // if slot modal is open, pre-select the new client
      if (modalOpen && editing) {
        setEditing({ ...editing, client_name: editingClient.name.trim(), client_phone: editingClient.phone.trim(), _client_id: (res as any).id });
      }
      toast.success("Cliente salvo");
      setClientOpen(false);
      setEditingClient({ name: "", cpf: "", phone: "" });
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const loadWeek = async () => {
    setLoading(true);
    try {
      const end = addDays(weekStart, 7);
      const r = await listBusyFn({ data: { weekStart: brIsoFrom(anchorIso(weekStart), 0), weekEnd: brIsoFrom(anchorIso(end), 0) } });
      setBusy(r as any);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStatic(); }, []);
  useEffect(() => { loadWeek(); }, [weekStart]);

  const slots = useMemo(() => {
    if (!settings) return [];
    const s = toMinutes((settings.start_time as string).slice(0, 5));
    const e = toMinutes((settings.end_time as string).slice(0, 5));
    const d = settings.slot_duration_min;
    const step = (settings as any).allow_half_hour_slots === false ? d : Math.min(30, d);
    const out: number[] = [];
    for (let m = s; m + d <= e; m += step) out.push(m);
    return out;
  }, [settings]);

  const daysBusy = useMemo(() => {
    const out: Busy[][] = Array.from({ length: 7 }, () => []);
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i);
      const isoDay = anchorIso(day);
      // rentals
      for (const r of busy.rentals) {
        if (brIsoDate(r.start_at) === isoDay) {
          out[i].push({
            kind: "rental", id: r.id, modalityId: r.modality_id ?? null,
            startMin: brMinutes(r.start_at),
            endMin: brMinutes(r.end_at),
            title: r.client_name,
            sub: r.rental_modalities?.name,
            rental_type: r.rental_type,
          });
        }
      }
      // trainings by day_of_week
      for (const t of busy.trainings) {
        if (t.day_of_week !== i) continue;
        if (t.start_date && isoDay < t.start_date) continue;
        if (t.end_date && isoDay > t.end_date) continue;
        out[i].push({
          kind: "training", id: t.id, modalityId: t.modality_id ?? null,
          startMin: toMinutes((t.start_time as string).slice(0, 5)),
          endMin: toMinutes((t.end_time as string).slice(0, 5)),
          title: t.name, sub: t.rental_modalities?.name ?? t.location ?? "Treino",
        });
      }
      // events
      for (const ev of busy.events) {
        if (ev.match_date !== isoDay) continue;
        if (!ev.start_time || !ev.end_time) continue;
        out[i].push({
          kind: "event", id: ev.id, modalityId: ev.modality_id ?? null,
          startMin: toMinutes((ev.start_time as string).slice(0, 5)),
          endMin: toMinutes((ev.end_time as string).slice(0, 5)),
          title: ev.championships?.name ?? "Evento",
          sub: ev.category ?? ev.field_name ?? "",
        });
      }
      // blackouts (one_off matches by date; weekly by day_of_week)
      for (const bl of busy.blackouts ?? []) {
        const matches = bl.kind === "one_off"
          ? bl.block_date === isoDay
          : bl.day_of_week === i;
        if (!matches) continue;
        out[i].push({
          kind: "blackout", id: bl.id, modalityId: bl.modality_id ?? null,
          startMin: toMinutes((bl.start_time as string).slice(0, 5)),
          endMin: toMinutes((bl.end_time as string).slice(0, 5)),
          title: bl.reason || "Indisponível",
          sub: bl.kind === "weekly" ? "Bloqueio semanal" : "Bloqueio pontual",
          blackoutKind: bl.kind,
        });
      }
    }
    return out;
  }, [busy, weekStart]);


  // Calendário mostra uma quadra/espaço por vez (filtro acima)
  const [calModId, setCalModId] = useState<string | null>(null);
  useEffect(() => {
    if (activeMods.length && !activeMods.some((m) => m.id === calModId)) {
      setCalModId(activeMods[0]!.id);
    }
  }, [activeMods, calModId]);
  const calCols = useMemo(
    () => [activeMods.find((m) => m.id === calModId) ?? null],
    [activeMods, calModId],
  );

  const findBusyList = (dayIdx: number, modId: string | null, slotStart: number) => {
    const slotEnd = slotStart + (settings?.slot_duration_min ?? 60);
    return daysBusy[dayIdx]
      .filter((b) => (modId == null || b.modalityId == null || b.modalityId === modId))
      .filter((b) => b.startMin < slotEnd && b.endMin > slotStart)
      .sort((a, b) => a.startMin - b.startMin);
  };

  const findBusy = (dayIdx: number, slotStart: number) => {
    const slotEnd = slotStart + (settings?.slot_duration_min ?? 60);
    return daysBusy[dayIdx].find((b) => b.startMin < slotEnd && b.endMin > slotStart);
  };

  const openSlot = (dayIdx: number, slotStart: number, modId?: string | null) => {
    const day = addDays(weekStart, dayIdx);
    const dayIso = anchorIso(day);
    const startIso = brIsoFrom(dayIso, slotStart);
    const endIso = brIsoFrom(dayIso, slotStart + (settings?.slot_duration_min ?? 60));
    setEditing({
      id: null,
      modality_id: modId ?? activeMods[0]?.id ?? null,
      rental_type: "avulsa",
      start_at: startIso,
      end_at: endIso,
      price_paid: 0, discount: 0,
      payment_method: (settings?.accepted_payment_methods ?? ["pix"])[0],
      client_name: "", client_phone: "", notes: "",
      status: "confirmada",
      _dateLabel: `${DAY_LABELS[dayIdx]} ${fmtDate(day)} · ${minutesToHHMM(slotStart)}–${minutesToHHMM(slotStart + (settings?.slot_duration_min ?? 60))}`,
    });
    setPastAck(false);
    setPastAction(null);
    setModalOpen(true);
  };

  const openRental = (id: string) => {
    const r = busy.rentals.find((x) => x.id === id);
    if (!r) return;
    setEditing({
      ...r,
      start_at: r.start_at, end_at: r.end_at,
      _dateLabel: `${brDateLabel(r.start_at)} ${brTime(r.start_at)}`,
    });
    setPastAck(false);
    setPastAction(null);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!editing?.modality_id) return;
    const typesAvailable = Array.from(new Set(
      prices.filter((p) => p.modality_id === editing.modality_id).map((p) => p.rental_type)
    )) as string[];
    if (typesAvailable.length && !typesAvailable.includes(editing.rental_type)) {
      setEditing((e: any) => ({ ...e, rental_type: typesAvailable[0] }));
      return;
    }
    if (editing.id) return;
    const st = editing.start_at ? new Date(editing.start_at) : null;
    const en = editing.end_at ? new Date(editing.end_at) : null;
    const dur = st && en ? Math.round((en.getTime() - st.getTime()) / 60000) : 60;
    const price = prices.find((p) =>
      p.modality_id === editing.modality_id &&
      p.rental_type === editing.rental_type &&
      (p.duration_min ?? 60) === dur,
    );
    if (price) {
      setEditing((e: any) => ({ ...e, price_paid: Number(price.price), discount: Number(price.discount) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.modality_id, editing?.rental_type, editing?.start_at, editing?.end_at]);

  // Locação retroativa: o CT pode lançar, mas precisa confirmar o aviso.
  const isPastSlot = () =>
    !!editing?.start_at && new Date(editing.start_at).getTime() < Date.now();

  const runWithPastCheck = (action: "save" | "confirm") => {
    if (isPastSlot() && !pastAck) {
      setPastAction(action);
      return;
    }
    if (action === "save") void save();
    else void confirmAndNotify();
  };

  const save = async () => {
    if (!editing.client_name?.trim()) return toast.error("Informe o cliente");

    try {
      const res: any = await saveFn({ data: {
        id: editing.id, modality_id: editing.modality_id,
        start_at: editing.start_at, end_at: editing.end_at,
        rental_type: editing.rental_type,
        price_paid: Number(editing.price_paid ?? 0),
        discount: Number(editing.discount ?? 0),
        payment_method: editing.payment_method ?? null,
        client_name: editing.client_name, client_phone: editing.client_phone ?? null,
        notes: editing.notes ?? null, status: editing.status ?? "confirmada",
      }});
      setModalOpen(false); loadWeek();
      toast.success("Locação salva");
      if (res?.conflicts?.length) {
        toast.warning(`Atenção: conflito de horário em ${res.conflicts.join(", ")}. Reorganize essas reservas.`, { duration: 8000 });
      }
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const remove = async () => {
    if (!editing?.id) return;
    if (!confirm("Excluir locação?")) return;
    try { await delFn({ data: { id: editing.id } }); setModalOpen(false); loadWeek(); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const total = Math.max(0, Number(editing?.price_paid ?? 0) - Number(editing?.discount ?? 0));

  const buildBaseMessage = (r: any) => {
    const mod = mods.find((m) => m.id === r.modality_id);
    const modName = mod?.name ?? "";
    const address = mod?.address ?? "";
    const scheduled: string[] = Array.isArray(r._scheduledDates) && r._scheduledDates.length > 0
      ? r._scheduledDates
      : [r.start_at];
    const sessions = r.rental_type === "mensal" ? scheduled.length : 1;
    const unit = Math.max(0, Number(r.price_paid ?? 0) - Number(r.discount ?? 0));
    const t = unit * sessions;
    const pm = PAYMENT_METHODS.find((m) => m.value === r.payment_method)?.label ?? (r.payment_method ?? "");

    const datesBlock = r.rental_type === "mensal" && scheduled.length > 1
      ? "📆 *Datas da mensalidade:*\n" + scheduled
          .map((iso) => `• ${brDateLabel(iso)} · ${brTime(iso)}`)
          .join("\n")
      : "";

    const totalLine = r.rental_type === "mensal" && sessions > 1
      ? `💰 Total mensal (${sessions}x R$ ${unit.toFixed(2)}): R$ ${t.toFixed(2)}`
      : `💰 Total: R$ ${t.toFixed(2)}${Number(r.discount) > 0 ? ` (desconto R$ ${Number(r.discount).toFixed(2)})` : ""}`;

    return [
      `Olá ${r.client_name?.split(" ")[0] ?? ""}! Sua reserva está confirmada ✅`,
      ``,
      r.rental_type !== "mensal" && `📅 ${brDateLabel(r.start_at)}`,
      `⏰ ${brTime(r.start_at)} às ${brTime(r.end_at)}`,
      modName && `🏟️ ${modName}`,
      address && `📍 ${address}`,
      (mod?.latitude != null && mod?.longitude != null) && `🗺️ https://www.google.com/maps?q=${mod.latitude},${mod.longitude}`,
      datesBlock,
      totalLine,
      pm && `💳 ${pm}`,
    ].filter(Boolean).join("\n");
  };

  const buildMessagesForRental = async (r: any): Promise<string[]> => {
    const base = buildBaseMessage(r);
    const scheduledCount = Array.isArray(r._scheduledDates) && r._scheduledDates.length > 0 ? r._scheduledDates.length : 1;
    const sessions = r.rental_type === "mensal" ? scheduledCount : 1;
    const amount = Math.max(0, Number(r.price_paid ?? 0) - Number(r.discount ?? 0)) * sessions;
    const channel = paymentMethodChannel(r.payment_method ?? "");
    const modName = mods.find((m) => m.id === r.modality_id)?.name ?? "Locação";
    const notes = (r.notes ?? "").toString().trim();

    let intro = base;
    let brcode = "";

    if (channel === "mercadopago" && amount > 0) {
      try {
        const tcRes: any = await pixInfoFn();
        const { data, error } = await supabase.functions.invoke("create-pix-payment", {
          body: {
            training_center_id: tcRes.id,
            transaction_amount: amount,
            description: `Locação — ${modName} — ${r.client_name}`,
            payer_email: "cliente@email.com",
            reference_id: `rental:${r.id ?? "novo"}`,
          },
        });
        if (error) throw error;
        const link = data?.init_point as string | undefined;
        brcode = (data?.qr_code as string | undefined) ?? "";
        intro +=
          `\n\n💳 *Pagamento via Mercado Pago* (${paymentMethodLabel(r.payment_method)})\n` +
          (link ? `Link: ${link}\n` : "") +
          `A baixa é feita automaticamente após a compensação.` +
          (brcode ? `\n\n📋 Pix *copia-e-cola* (toque e segure para copiar):` : "");
      } catch (e: any) {
        intro += `\n\n⚠️ Não foi possível gerar o link do Mercado Pago (${e?.message ?? "erro"}).`;
      }
    } else if (channel === "comprovante" && amount > 0) {
      try {
        const tc: any = await pixInfoFn();
        if (tc.pix_key) {
          brcode = buildPixBrCode({
            pixKey: tc.pix_key,
            beneficiaryName: tc.pix_beneficiary || tc.name || "PAGADOR",
            city: tc.city || "BRASIL",
            amount,
            txid: (r.id ?? `rental${Date.now()}`).toString().replace(/-/g, "").slice(0, 25),
            description: `Locacao ${modName}`.slice(0, 40),
          });
          intro +=
            `\n\n📎 *Após pagar, envie o comprovante neste mesmo número.*\n` +
            (tc.pix_beneficiary ? `Favorecido: *${tc.pix_beneficiary}*\n` : "") +
            `Pix *copia-e-cola* (valor já incluso) na próxima mensagem — toque e segure para copiar.`;
        } else {
          intro += `\n\n📎 *Após pagar, envie o comprovante neste mesmo número.*\nChave Pix não cadastrada.`;
        }
      } catch (e: any) {
        intro += `\n\n⚠️ ${e?.message ?? "Erro ao carregar chave Pix"}`;
      }
    } else if (channel === "presencial") {
      intro += `\n\n📍 Pagamento *presencial* — acerto no local.`;
    }

    const msgs: string[] = [intro];
    if (brcode) msgs.push(brcode);
    if (notes) msgs.push(`📝 *Observações:*\n${notes}`);
    return msgs;
  };


  const openWaModal = async (r: any) => {
    setWaModal({ rental: r, phone: r.client_phone ?? "", messages: [], loading: true, sending: false });
    try {
      const messages = await buildMessagesForRental(r);
      setWaModal((w) => (w ? { ...w, messages, loading: false } : w));
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao gerar mensagem");
      setWaModal((w) => (w ? { ...w, loading: false } : w));
    }
  };

  const sendWaApi = async () => {
    if (!waModal) return;
    const phone = (waModal.phone || "").trim();
    if (!phone) { toast.error("Informe o WhatsApp de destino."); return; }
    const toSend = waModal.messages.map((m) => m.trim()).filter(Boolean);
    if (!toSend.length) { toast.error("Nenhuma mensagem."); return; }
    setWaModal((w) => (w ? { ...w, sending: true } : w));
    try {
      const r: any = await sendSeqFn({ data: {
        recipient: { name: waModal.rental.client_name || "cliente", phone },
        messages: toSend,
      }});
      if (r.sent > 0) {
        toast.success(`${r.sent} mensagem(ns) enviada(s) via WhatsApp`);
        setWaModal(null);
      } else {
        toast.error(r.errors?.[0] || "Falha ao enviar");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar");
    } finally {
      setWaModal((w) => (w ? { ...w, sending: false } : w));
    }
  };

  const openWaWeb = () => {
    if (!waModal) return;
    const raw = (waModal.phone || "").replace(/\D/g, "");
    const num = raw ? (raw.length <= 11 ? "55" + raw : raw) : "";
    const text = encodeURIComponent(waModal.messages.join("\n\n"));
    window.open(num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
  };

  const confirmAndNotify = async () => {
    if (!editing.client_name?.trim()) return toast.error("Informe o cliente");
    try {
      const res: any = await saveFn({ data: {
        id: editing.id, modality_id: editing.modality_id,
        start_at: editing.start_at, end_at: editing.end_at,
        rental_type: editing.rental_type,
        price_paid: Number(editing.price_paid ?? 0),
        discount: Number(editing.discount ?? 0),
        payment_method: editing.payment_method ?? null,
        client_name: editing.client_name, client_phone: editing.client_phone ?? null,
        notes: editing.notes ?? null, status: "confirmada",
      }});
      const saved = { ...editing, id: editing.id ?? (res as any).id, _scheduledDates: (res as any)?.dates ?? [editing.start_at] };
      setModalOpen(false); loadWeek();
      toast.success("Locação confirmada");
      if (res?.conflicts?.length) {
        toast.warning(`Atenção: conflito de horário em ${res.conflicts.join(", ")}. Reorganize essas reservas.`, { duration: 8000 });
      }
      openWaModal(saved);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };


  const copyBusySlots = async () => {
    const lines: string[] = [`🗓️ Horários ocupados — ${fmtDate(weekStart)} a ${fmtDate(addDays(weekStart, 6))}`, ""];
    for (let i = 0; i < 7; i++) {
      const items = [...daysBusy[i]].sort((a, b) => a.startMin - b.startMin);
      if (!items.length) continue;
      const day = addDays(weekStart, i);
      lines.push(`*${DAY_LABELS[i]} ${fmtDate(day)}*`);
      for (const b of items) {
        const tag = b.kind === "rental" ? "🔴 Locado" : b.kind === "training" ? "🔵 Treino" : "🟣 Evento";
        lines.push(`  ${minutesToHHMM(b.startMin)}–${minutesToHHMM(b.endMin)} · ${tag} · ${b.title}${b.sub ? " (" + b.sub + ")" : ""}`);
      }
      lines.push("");
    }
    const text = lines.join("\n").trim() || "Nenhum horário ocupado nesta semana.";
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Horários copiados");
    } catch { toast.error("Não foi possível copiar"); }
  };

  const shareBusyWhatsapp = () => {
    const lines: string[] = [`🗓️ Horários ocupados — ${fmtDate(weekStart)} a ${fmtDate(addDays(weekStart, 6))}`, ""];
    for (let i = 0; i < 7; i++) {
      const items = [...daysBusy[i]].sort((a, b) => a.startMin - b.startMin);
      if (!items.length) continue;
      const day = addDays(weekStart, i);
      lines.push(`*${DAY_LABELS[i]} ${fmtDate(day)}*`);
      for (const b of items) {
        lines.push(`  ${minutesToHHMM(b.startMin)}–${minutesToHHMM(b.endMin)} — ${b.title}`);
      }
      lines.push("");
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const weekRentals = useMemo(() => {
    return [...busy.rentals].sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [busy.rentals]);

  const weekEnd = addDays(weekStart, 6);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle>Calendário Semanal</CardTitle>
          <CardDescription>
            Verde = livre · Vermelho = locado · Azul = treino · Roxo = evento · Cinza = bloqueado
          </CardDescription>

        </div>
        <div className="flex items-center gap-2">
          {activeMods.length > 0 && (
            <Select value={calModId ?? ""} onValueChange={(v) => setCalModId(v)}>
              <SelectTrigger className="w-[200px] h-9"><SelectValue placeholder="Quadra / Espaço" /></SelectTrigger>
              <SelectContent>{activeMods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-sm font-medium min-w-[180px] text-center">
            {fmtDate(weekStart)} — {fmtDate(weekEnd)}
          </div>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="secondary" size="sm" onClick={() => setWeekStart(brStartOfWeek(brToday()))}>Hoje</Button>
          <Button variant="outline" size="sm" onClick={copyBusySlots}><Copy className="h-4 w-4 mr-1" />Copiar ocupados</Button>
          <Button variant="outline" size="sm" onClick={shareBusyWhatsapp}><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</Button>
          <Button size="sm" onClick={() => { setEditingClient({ name: "", cpf: "", phone: "" }); setClientOpen(true); }}>
            <UserPlus className="h-4 w-4 mr-1" />Cadastrar cliente
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading || !settings ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
          <div className="overflow-auto">
            <table className="w-full border-collapse text-xs table-fixed">
              <thead>
                <tr>
                  <th className="border p-1 bg-muted/50 w-14" rowSpan={calCols.length > 1 ? 2 : 1}>Hora</th>
                  {Array.from({ length: 7 }).map((_, i) => {
                    const d = addDays(weekStart, i);
                    const today = anchorIso(d) === brToday();
                    return (
                      <th key={i} colSpan={calCols.length} className={`border p-1 ${today ? "bg-primary/10" : "bg-muted/50"}`}>
                        {DAY_LABELS[i]}<br /><span className="font-normal">{fmtDate(d)}</span>
                      </th>
                    );
                  })}
                </tr>
                {calCols.length > 1 && (
                  <tr>
                    {Array.from({ length: 7 }).flatMap((_, i) =>
                      calCols.map((m) => (
                        <th key={`${i}-${m?.id ?? "x"}`} className="border p-0.5 bg-muted/30 font-normal text-[10px] truncate">
                          {m?.name ?? "Espaço"}
                        </th>
                      )),
                    )}
                  </tr>
                )}
              </thead>
              <tbody>
                {slots.map((slotStart) => {
                  const dur = settings?.slot_duration_min ?? 60;
                  // Cada linha da grade representa o passo entre horários (30 min
                  // quando meia hora é permitida), não a duração da reserva —
                  // caso contrário os blocos ocupados ficam desalinhados.
                  const rowStep = (settings as any)?.allow_half_hour_slots === false ? dur : Math.min(30, dur);
                  const slotEnd = slotStart + dur;
                  const rowEnd = slotStart + rowStep;
                  return (
                    <tr key={slotStart}>
                      <td className="border p-1 text-center font-mono bg-muted/30">{minutesToHHMM(slotStart)}</td>
                      {Array.from({ length: 7 }).flatMap((_, dayIdx) =>
                        calCols.map((mod) => {
                          const day = addDays(weekStart, dayIdx);
                          const items = findBusyList(dayIdx, mod?.id ?? null, slotStart)
                            .filter((b) => b.startMin < rowEnd && b.endMin > slotStart);
                          const fullyBusy = items.some((b) => b.startMin <= slotStart && b.endMin >= rowEnd);
                          return (
                            <td key={`${dayIdx}-${mod?.id ?? "x"}`} className="border p-0 align-top">
                              <div className="relative h-12">
                                {!fullyBusy && (
                                  <div className="absolute inset-0 bg-green-50 hover:bg-green-100 text-green-700 flex items-start justify-between gap-1 p-1">
                                    <button className="flex-1 text-left cursor-pointer" onClick={() => openSlot(dayIdx, slotStart, mod?.id ?? null)}>
                                      + Reservar
                                    </button>
                                    <button
                                      className="p-0.5 rounded hover:bg-zinc-300 text-zinc-500 hover:text-zinc-800"
                                      title="Bloquear este horário"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setBlackoutModal({
                                          kind: "one_off",
                                          block_date: anchorIso(day),
                                          day_of_week: dayIdx,
                                          start_time: minutesToHHMM(slotStart),
                                          end_time: minutesToHHMM(slotEnd),
                                          reason: "",
                                          modality_id: mod?.id ?? null,
                                          _dateLabel: `${DAY_LABELS[dayIdx]} ${fmtDate(day)} · ${minutesToHHMM(slotStart)}–${minutesToHHMM(slotEnd)}`,
                                        });
                                      }}
                                    >
                                      <Ban className="h-3 w-3" />
                                    </button>
                                  </div>
                                )}
                                {items.map((b) => {
                                  const top = ((Math.max(b.startMin, slotStart) - slotStart) / rowStep) * 100;
                                  const height = ((Math.min(b.endMin, rowEnd) - Math.max(b.startMin, slotStart)) / rowStep) * 100;
                                  const color = b.kind === "rental" ? "bg-red-100 hover:bg-red-200 border-red-300"
                                    : b.kind === "training" ? "bg-blue-100 hover:bg-blue-200 border-blue-300"
                                    : b.kind === "blackout" ? "bg-zinc-200 hover:bg-zinc-300 border-zinc-400 [background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(0,0,0,0.06)_4px,rgba(0,0,0,0.06)_8px)]"
                                    : "bg-purple-100 hover:bg-purple-200 border-purple-300";
                                  const clickable = b.kind === "rental" || b.kind === "blackout";
                                  const onClick = b.kind === "rental" ? () => openRental(b.id)
                                    : b.kind === "blackout" ? () => {
                                        const bl = busy.blackouts.find((x: any) => x.id === b.id);
                                        if (!bl) return;
                                        setBlackoutModal({
                                          id: bl.id,
                                          kind: bl.kind,
                                          block_date: bl.block_date ?? anchorIso(day),
                                          day_of_week: bl.day_of_week ?? dayIdx,
                                          start_time: (bl.start_time as string).slice(0, 5),
                                          end_time: (bl.end_time as string).slice(0, 5),
                                          reason: bl.reason ?? "",
                                          modality_id: bl.modality_id ?? null,
                                          _dateLabel: `${DAY_LABELS[dayIdx]} ${fmtDate(day)}`,
                                        });
                                      }
                                    : undefined;
                                  return (
                                    <div
                                      key={`${b.kind}-${b.id}`}
                                      className={`absolute left-0 right-0 overflow-hidden border px-1 leading-tight ${color} ${clickable ? "cursor-pointer" : ""}`}
                                      style={{ top: `${top}%`, height: `${height}%` }}
                                      onClick={onClick}
                                      title={`${minutesToHHMM(b.startMin)}–${minutesToHHMM(b.endMin)} · ${b.title}`}
                                    >
                                      <div className="font-medium truncate flex items-center gap-1">
                                        {b.kind === "blackout" && <Ban className="h-3 w-3 shrink-0" />}
                                        <span className="truncate">{b.title}</span>
                                        {b.kind === "rental" && (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="ml-auto h-4 shrink-0 px-1 py-0 text-[10px] leading-none"
                                            title="Editar"
                                            onClick={(e) => { e.stopPropagation(); openRental(b.id); }}
                                          >
                                            <Pencil className="h-2.5 w-2.5" />
                                          </Button>
                                        )}
                                      </div>
                                      {b.sub && <div className="text-[10px] text-muted-foreground truncate">{b.sub}</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        }),
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <CardContent className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold">Reservas da semana</h3>
            <p className="text-xs text-muted-foreground">Gerencie as locações existentes desta semana.</p>
          </div>
          <Badge variant="secondary">{weekRentals.length} reserva{weekRentals.length === 1 ? "" : "s"}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Confirmada/Paga</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Pendente/Aguardando</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Mensal</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Avulsa</span>
        </div>
        {weekRentals.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhuma reserva nesta semana.</p>
        ) : (
          <div className="space-y-2">
            {weekRentals.map((r) => {
              const t = Math.max(0, Number(r.price_paid ?? 0) - Number(r.discount ?? 0));
              const statusLower = String(r.status ?? "").toLowerCase();
              const isConfirmed = ["confirmada", "confirmado", "paga"].includes(statusLower);
              const isPending = ["pendente", "aguardando"].includes(statusLower);
              const isMonthly = String(r.rental_type ?? "").toLowerCase() === "mensal";
              const borderColorClass = isConfirmed
                ? "border-l-emerald-500"
                : isPending
                ? "border-l-red-500"
                : isMonthly
                ? "border-l-amber-500"
                : "border-l-blue-500";
              return (
                <div key={r.id} className={`flex items-center justify-between gap-2 p-3 border border-l-4 rounded-md ${borderColorClass}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {r.client_name}
                      <Badge
                        variant="outline"
                        className={`ml-2 capitalize ${
                          isConfirmed
                            ? "bg-emerald-500/15 text-emerald-700 border-emerald-500"
                            : isPending
                            ? "bg-red-500/15 text-red-700 border-red-500"
                            : ""
                        }`}
                      >
                        {r.status}
                      </Badge>
                      {r.rental_type && (
                        <Badge
                          variant="outline"
                          className={`ml-1 capitalize ${
                            isMonthly
                              ? "bg-amber-500/15 text-amber-700 border-amber-500"
                              : "bg-blue-500/15 text-blue-700 border-blue-500"
                          }`}
                        >
                          {r.rental_type}
                        </Badge>
                      )}
                      {r.rental_modalities?.name && <Badge variant="secondary" className="ml-1">{r.rental_modalities.name}</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {DAY_LABELS[brDow(r.start_at)]} {brDateLabel(r.start_at)} · {brTime(r.start_at)}–{brTime(r.end_at)}
                      {" · "}R$ {t.toFixed(2)}
                      {r.client_phone && ` · ${r.client_phone}`}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" title="Mensagem WhatsApp" onClick={() => openWaModal(r)}>
                      <MessageCircle className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button variant="outline" size="sm" title="Editar" onClick={() => openRental(r.id)}>
                      <Pencil className="h-4 w-4 mr-1" />Editar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar" : "Nova"} locação</DialogTitle>
            {editing?._dateLabel && <p className="text-sm text-muted-foreground">{editing._dateLabel}</p>}
          </DialogHeader>
          {editing && (() => {
            const dayIso = editing.start_at ? brIsoDate(editing.start_at) : brToday();
            const curStartMin = editing.start_at ? brMinutes(editing.start_at) : 0;
            const curEndMin = editing.end_at ? brMinutes(editing.end_at) : 0;
            const curDuration = Math.max(15, curEndMin - curStartMin);
            const dateLabel = editing.start_at ? `${DAY_LABELS[brDow(editing.start_at)]}, ${brDateLabel(editing.start_at)}` : "";

            const pricesForMod = prices.filter((p) => p.modality_id === editing.modality_id);
            const typesAvailable = Array.from(new Set(pricesForMod.map((p) => p.rental_type))) as string[];
            const typesToShow = typesAvailable.length ? typesAvailable : ["avulsa", "mensal", "anual"];

            // End options: 30-min steps from start+30 up to end-of-day
            const dayEndMin = settings ? toMinutes((settings.end_time as string).slice(0, 5)) : 23 * 60;
            const endOptions: number[] = [];
            for (let m = curStartMin + 30; m <= dayEndMin; m += 30) endOptions.push(m);

            const hasPriceFor = (dur: number) =>
              !!pricesForMod.find((p) => p.rental_type === editing.rental_type && (p.duration_min ?? 60) === dur);

            const setDay = (newDayIso: string) => {
              setEditing({ ...editing, start_at: brIsoFrom(newDayIso, curStartMin), end_at: brIsoFrom(newDayIso, curStartMin + curDuration) });
            };
            const setStartMin = (m: number) => {
              setEditing({ ...editing, start_at: brIsoFrom(dayIso, m), end_at: brIsoFrom(dayIso, m + curDuration) });
            };
            const setEndMin = (m: number) => {
              setEditing({ ...editing, end_at: brIsoFrom(dayIso, m) });
              const dur = m - curStartMin;
              if (editing.modality_id && !hasPriceFor(dur) && !editing.id) {
                setMissingPrice({ duration_min: dur, price: "" });
              }
            };

            return (
              <div className="space-y-3">
                {dateLabel && <p className="text-sm text-muted-foreground">📅 {dateLabel}</p>}

                <div>
                  <Label>Data</Label>
                  <Input type="date" value={dayIso} onChange={(e) => setDay(e.target.value)} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Modalidade</Label>
                    <Select value={editing.modality_id ?? ""} onValueChange={(v) => setEditing({ ...editing, modality_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                      <SelectContent>{activeMods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select value={editing.rental_type} onValueChange={(v) => setEditing({ ...editing, rental_type: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                      <SelectContent>
                        {typesToShow.map((t) => (
                          <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Horário inicial</Label>
                    <Select value={String(curStartMin)} onValueChange={(v) => setStartMin(parseInt(v, 10))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {slots.map((m) => <SelectItem key={m} value={String(m)}>{minutesToHHMM(m)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Horário final <span className="text-muted-foreground font-normal">· {durationLabel(curDuration)}</span></Label>
                    <Select value={String(curEndMin)} onValueChange={(v) => setEndMin(parseInt(v, 10))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {endOptions.map((m) => {
                          const dur = m - curStartMin;
                          const missing = !hasPriceFor(dur);
                          return (
                            <SelectItem key={m} value={String(m)}>
                              {minutesToHHMM(m)} <span className="text-muted-foreground">· {durationLabel(dur)}{missing ? " (sem valor)" : ""}</span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Cliente cadastrado</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setEditingClient({ name: "", cpf: "", phone: "" }); setClientOpen(true); }}>
                      <UserPlus className="h-4 w-4 mr-1" />Cadastrar cliente
                    </Button>
                  </div>
                  <Select
                    value={editing._client_id ?? ""}
                    onValueChange={(v) => {
                      const c = clients.find((x) => x.id === v);
                      if (c) setEditing({ ...editing, _client_id: v, client_name: c.name, client_phone: c.phone ?? "" });
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder={clients.length ? "Selecionar cliente..." : "Nenhum cliente cadastrado"} /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` — ${c.phone}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Cliente *</Label>
                  <Input value={editing.client_name ?? ""} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={editing.client_phone ?? ""} onChange={(e) => setEditing({ ...editing, client_phone: e.target.value })} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={editing.price_paid} onChange={(e) => setEditing({ ...editing, price_paid: e.target.value })} /></div>
                  <div><Label>Desconto (R$)</Label><Input type="number" step="0.01" value={editing.discount} onChange={(e) => setEditing({ ...editing, discount: e.target.value })} /></div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md bg-primary/5 border border-primary/20">
                  <span className="text-sm font-medium">Total a cobrar</span>
                  <span className="text-lg font-bold text-primary">R$ {total.toFixed(2)}</span>
                </div>
                <div>
                  <Label>Forma de pagamento</Label>
                  <Select value={editing.payment_method ?? ""} onValueChange={(v) => setEditing({ ...editing, payment_method: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>
                      {(settings?.accepted_payment_methods ?? []).map((v: string) => {
                        const label = PAYMENT_METHODS.find((m) => m.value === v)?.label ?? v;
                        return <SelectItem key={v} value={v}>{label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Observações</Label>
                  <Textarea value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                </div>
              </div>
            );
          })()}
          <DialogFooter className="gap-2 flex-wrap">
            {editing?.id && <Button variant="destructive" onClick={remove}><Trash2 className="h-4 w-4 mr-1" />Excluir</Button>}
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="secondary" onClick={() => runWithPastCheck("save")}><Save className="h-4 w-4 mr-1" />Salvar</Button>
            <Button onClick={() => runWithPastCheck("confirm")} className="bg-green-600 hover:bg-green-700">

              <CheckCircle2 className="h-4 w-4 mr-1" />Confirmar e enviar WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aviso de locação retroativa */}
      <Dialog open={!!pastAction} onOpenChange={(o) => !o && setPastAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Locação em data que já passou</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>
              {editing?.start_at
                ? `Esta marcação é para ${brDateLabel(editing.start_at)} às ${brTime(editing.start_at)}, uma data/horário que já passou.`
                : "Esta marcação é para uma data que já passou."}
            </p>
            <p className="text-muted-foreground">
              O lançamento retroativo serve apenas para acertar o caixa e registrar a ocupação da quadra.
            </p>
            <p>Deseja realmente fazer esse lançamento?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPastAction(null)}>Cancelar</Button>
            <Button
              onClick={() => {
                const act = pastAction;
                setPastAck(true);
                setPastAction(null);
                if (act === "save") void save();
                else if (act === "confirm") void confirmAndNotify();
              }}
            >
              Sim, lançar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cadastrar cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={editingClient.name} onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })} />
            </div>
            <div>
              <Label>CPF</Label>
              <Input value={editingClient.cpf} onChange={(e) => setEditingClient({ ...editingClient, cpf: e.target.value })} placeholder="000.000.000-00" />
            </div>
            <div>
              <Label>WhatsApp *</Label>
              <Input value={editingClient.phone} onChange={(e) => setEditingClient({ ...editingClient, phone: e.target.value })} placeholder="(11) 90000-0000" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientOpen(false)}>Cancelar</Button>
            <Button onClick={saveClient}><Save className="h-4 w-4 mr-1" />Salvar cliente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!missingPrice} onOpenChange={(o) => !o && setMissingPrice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cadastrar valor para {missingPrice ? durationLabel(missingPrice.duration_min) : ""}</DialogTitle>
          </DialogHeader>
          {missingPrice && editing && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Não existe valor cadastrado para <strong>{durationLabel(missingPrice.duration_min)}</strong> em{" "}
                <strong>{mods.find((m) => m.id === editing.modality_id)?.name ?? "essa modalidade"}</strong> ({editing.rental_type}).
                Informe o valor para cadastrar e usar nesta locação.
              </p>
              <div>
                <Label>Valor (R$)</Label>
                <Input
                  type="number" step="0.01" autoFocus
                  value={missingPrice.price}
                  onChange={(e) => setMissingPrice({ ...missingPrice, price: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingPrice(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!missingPrice || !editing) return;
                const val = Number(missingPrice.price);
                if (!(val >= 0)) return toast.error("Informe um valor válido");
                try {
                  await savePriceFn({ data: {
                    id: null,
                    modality_id: editing.modality_id,
                    rental_type: editing.rental_type,
                    duration_min: missingPrice.duration_min,
                    price: val,
                    discount: 0,
                    payment_methods: settings?.accepted_payment_methods ?? ["pix", "cartao"],
                  }});
                  const p = await listPricesFn();
                  setPrices(p.prices);
                  setEditing({ ...editing, price_paid: val });
                  toast.success("Valor cadastrado");
                  setMissingPrice(null);
                } catch (e: any) { toast.error(e?.message ?? "Erro"); }
              }}
            >
              <Save className="h-4 w-4 mr-1" />Salvar valor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!waModal} onOpenChange={(o) => !o && setWaModal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mensagem WhatsApp — Locação</DialogTitle>
          </DialogHeader>
          {waModal && (
            <div className="space-y-3">
              <div>
                <Label>WhatsApp do cliente</Label>
                <Input
                  value={waModal.phone}
                  onChange={(e) => setWaModal({ ...waModal, phone: e.target.value })}
                  placeholder="(11) 90000-0000"
                />
              </div>
              {waModal.loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Gerando mensagem e link de pagamento…
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Prévia (edite se quiser)</Label>
                  {waModal.messages.map((m, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <Textarea
                        rows={Math.min(10, Math.max(3, m.split("\n").length + 1))}
                        value={m}
                        onChange={(e) => setWaModal({
                          ...waModal,
                          messages: waModal.messages.map((x, idx) => (idx === i ? e.target.value : x)),
                        })}
                      />
                      <Button
                        variant="ghost" size="icon" title="Copiar"
                        onClick={async () => { try { await navigator.clipboard.writeText(m); toast.success("Copiado"); } catch {} }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    Cada bloco é enviado como uma mensagem separada (facilita copiar o Pix ou o link).
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setWaModal(null)}>Fechar</Button>
            <Button variant="secondary" onClick={openWaWeb} disabled={!waModal || waModal.loading}>
              <ExternalLink className="h-4 w-4 mr-1" />WhatsApp Web
            </Button>
            <Button
              onClick={sendWaApi}
              disabled={!waModal || waModal.loading || waModal.sending}
              className="bg-green-600 hover:bg-green-700"
            >
              {waModal?.sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Enviar via API
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!blackoutModal} onOpenChange={(o) => !o && setBlackoutModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{blackoutModal?.id ? "Editar bloqueio" : "Bloquear horário"}</DialogTitle>
            {blackoutModal?._dateLabel && <p className="text-sm text-muted-foreground">{blackoutModal._dateLabel}</p>}
          </DialogHeader>
          {blackoutModal && (
            <div className="space-y-3">
              <div>
                <Label className="mb-2 block">Tipo de bloqueio</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className={`p-3 border rounded-md text-sm text-left ${blackoutModal.kind === "one_off" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-secondary/30"}`}
                    onClick={() => setBlackoutModal({ ...blackoutModal, kind: "one_off" })}
                  >
                    <div className="font-medium">Pontual</div>
                    <div className="text-xs text-muted-foreground">Somente nesta data</div>
                  </button>
                  <button
                    type="button"
                    className={`p-3 border rounded-md text-sm text-left ${blackoutModal.kind === "weekly" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-secondary/30"}`}
                    onClick={() => setBlackoutModal({ ...blackoutModal, kind: "weekly" })}
                  >
                    <div className="font-medium">Semanal fixo</div>
                    <div className="text-xs text-muted-foreground">Todo {DAY_LABELS[blackoutModal.day_of_week]}. neste horário</div>
                  </button>
                </div>
              </div>

              {blackoutModal.kind === "one_off" ? (
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={blackoutModal.block_date}
                    onChange={(e) => setBlackoutModal({ ...blackoutModal, block_date: e.target.value })} />
                </div>
              ) : (
                <div>
                  <Label>Dia da semana</Label>
                  <Select value={String(blackoutModal.day_of_week)} onValueChange={(v) => setBlackoutModal({ ...blackoutModal, day_of_week: parseInt(v, 10) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAY_LABELS.map((l, i) => <SelectItem key={i} value={String(i)}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Das</Label>
                  <Input type="time" value={blackoutModal.start_time}
                    onChange={(e) => setBlackoutModal({ ...blackoutModal, start_time: e.target.value })} />
                </div>
                <div>
                  <Label>Até</Label>
                  <Input type="time" value={blackoutModal.end_time}
                    onChange={(e) => setBlackoutModal({ ...blackoutModal, end_time: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Espaço (opcional)</Label>
                <Select
                  value={blackoutModal.modality_id ?? "__all__"}
                  onValueChange={(v) => setBlackoutModal({ ...blackoutModal, modality_id: v === "__all__" ? null : v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os espaços</SelectItem>
                    {activeMods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Motivo (opcional)</Label>
                <Input placeholder="Ex.: Manutenção, feriado, reservado interno…"
                  value={blackoutModal.reason}
                  onChange={(e) => setBlackoutModal({ ...blackoutModal, reason: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            {blackoutModal?.id && (
              <Button variant="destructive" onClick={async () => {
                if (!confirm("Remover este bloqueio?")) return;
                try {
                  await deleteBlackoutFn({ data: { id: blackoutModal.id! } });
                  toast.success("Bloqueio removido");
                  setBlackoutModal(null); loadWeek();
                } catch (e: any) { toast.error(e?.message ?? "Erro"); }
              }}>
                <Trash2 className="h-4 w-4 mr-1" />Remover
              </Button>
            )}
            <Button variant="outline" onClick={() => setBlackoutModal(null)}>Cancelar</Button>
            <Button onClick={async () => {
              if (!blackoutModal) return;
              if (blackoutModal.end_time <= blackoutModal.start_time) {
                return toast.error("Horário final deve ser maior que o inicial");
              }
              try {
                await saveBlackoutFn({ data: {
                  id: blackoutModal.id ?? null,
                  kind: blackoutModal.kind,
                  block_date: blackoutModal.kind === "one_off" ? blackoutModal.block_date : null,
                  day_of_week: blackoutModal.kind === "weekly" ? blackoutModal.day_of_week : null,
                  start_time: blackoutModal.start_time,
                  end_time: blackoutModal.end_time,
                  reason: blackoutModal.reason || null,
                  modality_id: blackoutModal.modality_id,
                }});
                toast.success("Bloqueio salvo");
                setBlackoutModal(null); loadWeek();
              } catch (e: any) { toast.error(e?.message ?? "Erro"); }
            }}>
              <Save className="h-4 w-4 mr-1" />Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>


  );
}
